import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  getBrokerWebhookConfig,
  saveBrokerWebhookConfig,
  getBrokerWebhookEvents,
  retryBrokerWebhookEvent,
} from "api/brokerApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /brokers/:brokerId/webhook — configure inbound webhook for a broker, copy
 * the HMAC secret, and watch incoming events flow in for triage. The secret
 * is shown in plaintext only on rotate; after that it's masked forever.
 */

const STATUS_COLOR = {
  received: "#0ea5e9",
  processed: "#16a34a",
  duplicate: "#94a3b8",
  error: "#dc2626",
};

const fmt = (ms) => (ms ? new Date(ms).toLocaleString() : "—");

export const BrokerWebhookPage = () => {
  const { brokerId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [revealedSecret, setRevealedSecret] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, e] = await Promise.all([
        getBrokerWebhookConfig(brokerId),
        getBrokerWebhookEvents(brokerId),
      ]);
      setConfig(unwrapObject(c));
      setEvents(unwrapList(e));
    } catch (err) {
      setError(err?.message || "Failed to load webhook config");
    } finally {
      setLoading(false);
    }
  }, [brokerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async ({ enabled, rotate }) => {
    setSaving(true);
    setError(null);
    setRevealedSecret(null);
    try {
      const res = await saveBrokerWebhookConfig(brokerId, { enabled, rotate });
      const c = unwrapObject(res);
      if (!c) throw new Error("Save failed");
      setConfig({ ...c, secret: rotate ? c.secret : config?.secret });
      if (rotate && c.secret) setRevealedSecret(c.secret);
    } catch (e) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
  };

  const webhookUrl = `${window.location.origin.replace("app.", "api.")}/api/v1/broker-webhooks/${brokerId.slice(0, 8)}`;
  // ^^ Approximation — operator should use the broker code, not id; UI shows code if available

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Webhook ingest
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Receive booking events from this broker. The broker signs every request
            with HMAC-SHA256 using your shared secret; we verify, dedupe by external
            id, and persist for review or downstream processing.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && !config ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !config ? null : (
        <>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Configuration
              </Typography>

              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!config.enabled}
                      onChange={(e) =>
                        handleSave({ enabled: e.target.checked, rotate: false })
                      }
                      disabled={saving}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Enabled — accept incoming webhook requests for this broker
                    </Typography>
                  }
                />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Webhook URL to share with the broker
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      size="small"
                      value={webhookUrl}
                      InputProps={{ readOnly: true }}
                      sx={{ flex: 1, maxWidth: 600 }}
                    />
                    <IconButton size="small" onClick={() => copy(webhookUrl)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Shared HMAC secret
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <TextField
                      size="small"
                      value={revealedSecret || config.secret || "(not set)"}
                      InputProps={{ readOnly: true }}
                      sx={{ flex: 1, maxWidth: 600, fontFamily: "monospace" }}
                    />
                    {revealedSecret ? (
                      <IconButton size="small" onClick={() => copy(revealedSecret)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        if (
                          config.secret &&
                          !window.confirm("Rotating the secret will break any broker that still has the old one. Continue?")
                        ) return;
                        handleSave({ enabled: true, rotate: true });
                      }}
                      disabled={saving}
                      sx={{ backgroundColor: "#1F4E78" }}
                    >
                      {config.secret ? "Rotate secret" : "Generate secret"}
                    </Button>
                  </Box>
                  {revealedSecret ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Copy this secret now — we won't show it again. Update the broker's webhook configuration with this exact value.
                    </Alert>
                  ) : null}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Recent events ({events.length})
              </Typography>
              {events.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                  No events received yet. Give the broker the URL + secret above and ask them to fire a test event.
                </Typography>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Received</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Signature</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>External ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((e) => (
                        <TableRow key={e.id} hover>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(e.receivedAtMs)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={e.status}
                              sx={{
                                backgroundColor: STATUS_COLOR[e.status] || "#64748b",
                                color: "#fff",
                                fontWeight: 600,
                                height: 22,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={e.signatureValid ? "valid" : "invalid"}
                              sx={{
                                backgroundColor: e.signatureValid ? "#dcfce7" : "#fee2e2",
                                color: e.signatureValid ? "#166534" : "#991b1b",
                                height: 20,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                              {e.externalId || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="caption" color="text.secondary">{e.errorMessage || "—"}</Typography>
                          </TableCell>
                          <TableCell>
                            {e.signatureValid ? (
                              <IconButton
                                size="small"
                                title={e.status === "processed" ? "Re-process" : "Retry"}
                                onClick={async () => {
                                  try {
                                    await retryBrokerWebhookEvent(brokerId, e.id);
                                    load();
                                  } catch (err) {
                                    setError(err?.message || "Retry failed");
                                  }
                                }}
                              >
                                <ReplayIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
};

export default BrokerWebhookPage;
